<template>
  <div class="row">
    <wa-tabs />
    <div class="col-12 col-lg-8 c">
      <div class="card card-body text-center" v-if="!loaded">
        <img :src="require('@/assets/images/loading.svg')" class="c" style="width: 120px; height: 120px" alt="جاري التحميل" />
      </div>

      <div class="alert alert-danger text-center g" v-if="loaded && error">{{ error }}</div>

      <!-- the service account is not created yet: /info (the QR page) creates it -->
      <div class="card card-body text-center" v-if="loaded && !error && !info.linked">
        <i class="fa fa-whatsapp wa-big text-success"></i>
        <h4 class="g">لم يتم ربط خدمة الواتساب بعد</h4>
        <p class="text-muted">افتح صفحة «فحص الجهاز» وامسح الرمز من تطبيق واتساب على جوال المدرسة.</p>
        <div>
          <router-link to="/_whatsapp" class="btn btn-primary"><i class="fa fa-qrcode"></i> فحص الجهاز</router-link>
        </div>
      </div>

      <div class="card card-body text-center" v-if="loaded && !error && info.linked && !info.device">
        <i class="fa fa-mobile wa-big text-muted"></i>
        <h4 class="g">لا يوجد جهاز مرتبط</h4>
        <p class="text-muted">أضف جهازاً من صفحة «فحص الجهاز» ثم امسح رمز QR من تطبيق واتساب.</p>
        <div>
          <router-link to="/_whatsapp" class="btn btn-primary"><i class="fa fa-plus"></i> ربط جهاز جديد</router-link>
        </div>
      </div>

      <div class="card" v-if="loaded && !error && info.device">
        <div class="card-body">
          <div class="wa-head">
            <img v-if="info.device.image" :src="info.device.image" class="wa-avatar" alt="صورة حساب الواتساب" />
            <div v-else class="wa-avatar wa-avatar-empty"><i class="fa fa-whatsapp"></i></div>
            <div class="wa-head-text">
              <h4 class="wa-title">{{ info.device.title || 'جهازي' }}</h4>
              <div class="wa-sub" v-if="info.device.bio">{{ info.device.bio }}</div>
              <div class="wa-pills">
                <span :class="'wa-pill ' + statusClass(info.device.status)">
                  <i :class="'fa ' + statusIcon(info.device.status)"></i>
                  {{ info.device.status_text }}
                </span>
                <span class="wa-pill wa-pill-info" v-if="info.device.slow_mode">
                  <i class="fa fa-shield"></i> وضع الحماية فعّال
                </span>
              </div>
            </div>
          </div>

          <table class="table table-borderless wa-facts">
            <tbody>
              <tr>
                <th>رقم الجوال</th>
                <td><bdi dir="ltr">{{ info.device.phone || '—' }}</bdi></td>
              </tr>
              <tr v-if="info.device.add_date">
                <th>تاريخ الإضافة</th>
                <td><bdi dir="ltr">{{ shortDate(info.device.add_date) }}</bdi></td>
              </tr>
              <tr v-if="info.stats">
                <th>رسائل أُرسلت اليوم</th>
                <td>{{ info.stats.today_messages }}</td>
              </tr>
              <tr v-if="info.stats">
                <th>في انتظار الإرسال</th>
                <td>
                  {{ info.stats.pending_messages }}
                  <router-link v-if="info.stats.pending_messages > 0" to="/_whatsapp/archive" class="wa-link">عرض الأرشيف</router-link>
                </td>
              </tr>
              <tr>
                <th>آخر تحديث</th>
                <td><bdi dir="ltr">{{ updatedAt }}</bdi></td>
              </tr>
            </tbody>
          </table>

          <div class="alert alert-warning g" v-if="info.device.status == 2 || info.device.status == 0">
            {{ info.device.status == 0 ? 'الجهاز لم يُربط بعد: افتح «فحص الجهاز» وامسح رمز QR.' : 'انقطع اتصال الجهاز: جرّب إعادة التشغيل، وإن لم يعد فاحذفه واربطه من جديد.' }}
          </div>
          <div class="alert alert-danger g" v-if="info.device.status == 3">
            هذا الرقم جديد على واتساب، وقد يُحظر إذا أُرسل منه عدد كبير من الرسائل. تواصل مع الدعم الفني لمزيد من التفاصيل.
          </div>

          <div class="alert alert-success g" v-if="done">{{ done }}</div>
          <div class="alert alert-danger g" v-if="actionError">{{ actionError }}</div>

          <!-- in-page confirm instead of confirm() -->
          <div class="wa-confirm" v-if="confirming">
            <p class="mb-1"><strong>{{ confirming == 'delete' ? 'فصل الجهاز وحذفه من الخدمة؟' : 'إعادة تشغيل الجهاز؟' }}</strong></p>
            <p class="text-muted mb-1" v-if="confirming == 'delete'">تتوقف رسائل الواتساب التلقائية حتى تربط جهازاً جديداً.</p>
            <button class="btn btn-sm" :class="confirming == 'delete' ? 'btn-danger' : 'btn-warning'" :disabled="busy" @click="runAction()">
              <i class="fa fa-check"></i> {{ confirming == 'delete' ? 'نعم، احذف الجهاز' : 'نعم، أعد التشغيل' }}
            </button>
            <button class="btn btn-sm btn-outline-secondary" :disabled="busy" @click="confirming = null">تراجع</button>
          </div>

          <div class="wa-actions" v-else>
            <button class="btn btn-outline-warning" :disabled="busy" @click="ask('restart')">
              <i class="fa fa-refresh"></i> إعادة تشغيل
            </button>
            <button class="btn btn-outline-danger" :disabled="busy" @click="ask('delete')">
              <i class="fa fa-unlink"></i> فصل وحذف الجهاز
            </button>
            <router-link to="/_whatsapp" class="btn btn-outline-primary">
              <i class="fa fa-qrcode"></i> {{ info.device.status == 1 ? 'صفحة فحص الجهاز' : 'ربط الجهاز (QR)' }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WaTabs from './wa-tabs.vue';

export default {
  components: { WaTabs },
  data() {
    return {
      user: JSON.parse(localStorage.getItem('user')),
      info: {},
      loaded: false,
      error: null,
      busy: false,
      confirming: null,
      done: null,
      actionError: null,
      updatedAt: '',
      interval: null,
    };
  },
  created() {
    if (!checkPer('whatsapp')) {
      this.$router.push('/NotPermitted');
      return false;
    }
    this.load();
    this.interval = setInterval(this.load, 10000);
  },
  beforeDestroy() {
    clearInterval(this.interval);
  },
  methods: {
    load() {
      var g = this;
      $.post(api + '/user/whatsapp/device', { jwt: g.user.jwt })
        .then(function (r) {
          g.loaded = true;
          if (r.status != 100) {
            g.error = r.response;
            return;
          }
          g.error = null;
          g.info = r.response;
          g.updatedAt = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        })
        .fail(function () {
          g.loaded = true;
          g.error = 'تعذر الاتصال بالخادم، تحقق من الإنترنت';
        });
    },
    ask(what) {
      this.done = null;
      this.actionError = null;
      this.confirming = what;
    },
    runAction() {
      var g = this, what = g.confirming;
      g.busy = true;
      $.post(api + '/user/whatsapp/' + (what == 'delete' ? 'delete' : 'restart'), { jwt: g.user.jwt })
        .then(function (r) {
          g.busy = false;
          g.confirming = null;
          if (r.status != 100) {
            g.actionError = r.response;
            return;
          }
          g.done = what == 'delete' ? 'تم حذف الجهاز. اربط جهازاً جديداً من صفحة «فحص الجهاز».' : 'تم طلب إعادة التشغيل، قد يستغرق دقيقة.';
          g.load();
        })
        .fail(function () {
          g.busy = false;
          g.confirming = null;
          g.actionError = 'تعذر الاتصال بالخادم، حاول مرة أخرى';
        });
    },
    statusClass(s) {
      return s == 1 ? 'wa-pill-ok' : s == 0 ? 'wa-pill-wait' : 'wa-pill-bad';
    },
    statusIcon(s) {
      return s == 1 ? 'fa-check' : s == 0 ? 'fa-clock-o' : 'fa-times';
    },
    shortDate(d) {
      return (d || '').toString().replace('T', ' ').split('.')[0];
    },
  },
};
</script>

<style scoped>
.wa-big {
  font-size: 56px;
  margin-top: 8px;
}
.wa-head {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}
.wa-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 72px;
}
.wa-avatar-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  color: #12b76a;
  background: rgba(18, 183, 106, 0.12);
}
.wa-head-text {
  min-width: 0;
}
.wa-title {
  margin: 0 0 2px;
}
.wa-sub {
  color: #6b7280;
  font-size: 13px;
  margin-bottom: 6px;
  overflow-wrap: anywhere;
}
.wa-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.wa-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
}
.wa-pill-ok {
  background: rgba(18, 183, 106, 0.14);
  color: #067647;
}
.wa-pill-wait {
  background: rgba(247, 144, 9, 0.16);
  color: #b54708;
}
.wa-pill-bad {
  background: rgba(240, 68, 56, 0.14);
  color: #b42318;
}
.wa-pill-info {
  background: rgba(29, 78, 216, 0.1);
  color: #1d4ed8;
}
.wa-facts th {
  width: 40%;
  color: #6b7280;
  font-weight: 600;
}
.wa-facts th,
.wa-facts td {
  padding: 8px 4px;
  border-bottom: 1px dashed rgba(107, 114, 128, 0.25);
}
.wa-link {
  margin-inline-start: 8px;
  font-size: 13px;
}
.wa-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.wa-confirm {
  border: 1px solid rgba(240, 68, 56, 0.35);
  border-radius: 10px;
  padding: 12px;
}
.wa-confirm .btn {
  margin-top: 6px;
  margin-inline-end: 6px;
}
@media (max-width: 576px) {
  .wa-actions .btn {
    flex: 1 1 100%;
  }
}
</style>
